import { createTheme } from '@mui/material/styles';
import { lightTokens, darkTokens } from './tokens';

/**
 * Build a MUI theme from the same token source of truth as the CSS variables.
 * `mode` is 'light' | 'dark'. Palette values and component styleOverrides both
 * read from tokens[mode], so MUI surfaces track the app theme exactly.
 *
 * Only the components the audit flagged as needing explicit dark treatment
 * (surfaces / elevation / borders / hardcoded-light backgrounds) get overrides;
 * palette-driven components (Button, Typography, Grid, etc.) inherit automatically.
 */
export function buildMuiTheme(mode = 'light') {
  const t = mode === 'dark' ? darkTokens : lightTokens;

  return createTheme({
    palette: {
      mode,
      primary: { main: t.accent.primary },
      success: { main: t.status.success },
      warning: { main: t.status.warning },
      error: { main: t.status.error },
      info: { main: t.status.info },
      divider: t.border.color,
      background: {
        default: t.bg.primary,
        paper: t.bg.elevated
      },
      text: {
        primary: t.text.primary,
        secondary: t.text.secondary,
        disabled: t.text.disabled
      }
    },
    components: {
      // Global reset that complements the CSS-var layer (scrollbars, selection).
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            // Dark page uses the darkest surface (bg.primary) so elevated cards
            // separate from it; light keeps its lighter page bg.
            backgroundColor: mode === 'dark' ? t.bg.primary : t.bg.secondary,
            color: t.text.primary
          }
        }
      },
      // Paper is the base elevation surface — kill the elevation overlay so dark
      // paper does not drift lighter with elevation, and pin the surface color.
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundColor: t.bg.elevated,
            backgroundImage: 'none',
            color: t.text.primary
          }
        }
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundColor: t.bg.elevated,
            borderColor: t.border.color
          }
        }
      },
      MuiDialog: {
        styleOverrides: {
          paper: { backgroundColor: t.bg.elevated }
        }
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            backgroundColor: t.bg.elevated,
            border: `1px solid ${t.border.color}`
          }
        }
      },
      MuiPopover: {
        styleOverrides: {
          paper: { backgroundColor: t.bg.elevated }
        }
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: t.bg.elevated,
            borderColor: t.border.color
          }
        }
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-notchedOutline': { borderColor: t.input.border },
            '& .MuiInputBase-input': { color: t.input.text },
            '& .MuiInputBase-input::placeholder': { color: t.input.placeholder, opacity: 1 }
          }
        }
      },
      MuiOutlinedInput: {
        styleOverrides: {
          notchedOutline: { borderColor: t.input.border },
          root: {
            backgroundColor: t.input.bg,
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: t.text.tertiary },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: t.input['focus-border']
            }
          }
        }
      },
      MuiTableCell: {
        styleOverrides: {
          root: { borderColor: t.table.border },
          head: {
            backgroundColor: t.table['header-bg'],
            color: t.text.secondary
          }
        }
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            '&:hover': { backgroundColor: t.table['row-hover'] }
          }
        }
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: t.bg.tertiary,
            color: t.text.primary,
            border: `1px solid ${t.border.color}`
          },
          arrow: { color: t.bg.tertiary }
        }
      },
      MuiChip: {
        styleOverrides: {
          // default (unstyled) variant only — colored variants inherit palette
          root: {
            '&.MuiChip-colorDefault': {
              backgroundColor: t.bg.tertiary,
              color: t.text.primary
            }
          },
          outlined: { borderColor: t.border.color }
        }
      }
    }
  });
}

export default buildMuiTheme;
